import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { SiteConfigService, SiteThemePalette, ThemeMode } from './site-config.service';

type EffectiveThemeMode = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeModeService {
  private readonly site = inject(SiteConfigService);
  readonly selectedMode = signal<ThemeMode>('auto');
  readonly systemMode = signal<EffectiveThemeMode>('light');
  readonly previewMode = signal<EffectiveThemeMode | null>(null);
  readonly effectiveMode = computed<EffectiveThemeMode>(() => {
    const locked = this.site.config().theme.lockedMode;
    if (locked) return locked;
    const preview = this.previewMode();
    if (preview) return preview;
    const selected = this.selectedMode();
    return selected === 'auto' ? this.systemMode() : selected;
  });

  constructor() {
    if (typeof window !== 'undefined') {
      const requested = new URL(window.location.href).searchParams.get('theme_mode');
      if (requested === 'light' || requested === 'dark') this.previewMode.set(requested);
    }
    this.watchSystemMode();
    void this.site.ready().then(() => this.loadPreference());

    effect(() => this.applyTheme());
  }

  setMode(mode: ThemeMode): void {
    const locked = this.site.config().theme.lockedMode;
    if (locked) {
      this.selectedMode.set(locked);
      return;
    }
    this.selectedMode.set(mode);

    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(this.storageKey(), mode);
  }

  private loadPreference(): void {
    const locked = this.site.config().theme.lockedMode;
    if (locked) {
      this.selectedMode.set(locked);
      return;
    }
    const defaultMode = this.site.config().theme.defaultMode ?? 'auto';
    const stored = this.readStoredMode();
    this.selectedMode.set(stored ?? defaultMode);
  }

  private readStoredMode(): ThemeMode | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const value = localStorage.getItem(this.storageKey());
    return value === 'light' || value === 'dark' || value === 'auto' ? value : null;
  }

  private storageKey(): string {
    return `aymudo.themeMode.${this.site.config().brandKey}`;
  }

  private watchSystemMode(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => this.systemMode.set(media.matches ? 'dark' : 'light');

    update();
    media.addEventListener('change', update);
  }

  private applyTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const config = this.site.config();
    const palette = this.effectiveMode() === 'dark' ? config.theme.dark : config.theme;
    const root = document.documentElement;

    root.style.setProperty('--accent', palette.accent);
    root.style.setProperty('--accent-contrast', this.contrastText(palette.accent));
    root.style.setProperty('--ink', palette.ink);
    root.style.setProperty('--paper', palette.paper);
    root.style.setProperty('--surface', palette.surface);
    root.style.setProperty('--muted', this.withAlpha(palette.ink, this.effectiveMode() === 'dark' ? 0.72 : 0.68));
    root.style.setProperty('--border', this.withAlpha(palette.ink, this.effectiveMode() === 'dark' ? 0.16 : 0.1));
    root.style.setProperty('--card', this.cardSurface(palette));
    root.style.setProperty('--shadow', this.effectiveMode() === 'dark' ? 'rgb(0 0 0 / 32%)' : this.withAlpha(palette.ink, 0.09));
    root.style.setProperty('--glow', this.withAlpha(palette.accent, this.effectiveMode() === 'dark' ? 0.28 : 0.2));
    root.dataset['themeMode'] = this.selectedMode();
    root.dataset['effectiveTheme'] = this.effectiveMode();
    root.style.colorScheme = this.effectiveMode();
  }

  private cardSurface(palette: SiteThemePalette): string {
    return this.effectiveMode() === 'dark'
      ? `color-mix(in srgb, ${palette.surface} 78%, black)`
      : `color-mix(in srgb, ${palette.surface} 72%, white)`;
  }

  private withAlpha(color: string, alpha: number): string {
    return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
  }

  private contrastText(color:string):string {
    const match=/^#([0-9a-f]{6})$/i.exec(String(color).trim());
    if(!match)return this.effectiveMode()==='dark'?'#07110f':'#ffffff';
    const rgb=[0,2,4].map(index=>parseInt(match[1].slice(index,index+2),16)/255).map(value=>value<=.03928?value/12.92:Math.pow((value+.055)/1.055,2.4));
    const luminance=.2126*rgb[0]+.7152*rgb[1]+.0722*rgb[2];
    return luminance>.19?'#07110f':'#ffffff';
  }
}
